import { useEffect, useMemo, Suspense, forwardRef, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import type { EditorRegistration } from '../client/editor-registry.js'

// ─── Mounting a registry editor (#942) ──────────────────────────────────
//
// The ONE place a surface turns "this registration, editable or not" into a
// mounted editor — the drive's EditorHost (workspace tab, /file/:id) and the
// share view both go through it, so the rules below can't differ by surface:
//   - the registration's `defaultProps` are applied (memoised: no remounts);
//   - save / discard / dirty / undo hooks reach the editor ONLY when it may
//     write — a read-only editor offers nothing to save;
//   - a flip to read-only clears the surface's dirty mark (I116), and the
//     surface's `mountKey` (which carries ro/rw) remounts the editor;
//   - preview-only (over the live editor's size limit): no live room, the
//     editor's preview of the last saved version, with the surface's note.
// What differs by surface or family (a share link's caps, `hideRun`, the share
// session's preview id…) is passed as `extra`, never decided here.

export interface EditorMountProps {
  registration: EditorRegistration
  /** Remount key — include whatever must remount the editor (ro/rw, a session). */
  mountKey: string
  fileId: string
  fileName: string
  /** The live collab room ('' = none: snapshot / local mode). */
  room: string
  readOnly: boolean
  /** Over the size limit: the editor's preview-only mode (HtmlEditor). */
  previewOnly?: boolean
  /** Shown in a preview-only editor's toolbar — says why. */
  notice?: ReactNode
  isActive?: boolean
  onDirtyChange?: (dirty: boolean) => void
  saveRef?: React.MutableRefObject<(() => void) | null>
  discardRef?: React.MutableRefObject<(() => void) | null>
  registerUndoRedo?: unknown
  /** Surface / family props, passed through as they are. */
  extra?: Record<string, unknown>
  fallback?: ReactNode
}

const DefaultSpinner = (
  <div className="flex-1 flex items-center justify-center">
    <Loader2 className="animate-spin themed-text-muted" size={24} />
  </div>
)

/** I116 — a live flip to read-only remounts the editor without its dirty/save
 *  hooks; clear the surface's dirty mark so it doesn't keep offering a Save. */
function ClearDirtyWhenReadOnly({ readOnly, onDirtyChange }: { readOnly: boolean; onDirtyChange?: (dirty: boolean) => void }) {
  useEffect(() => { if (readOnly) onDirtyChange?.(false) }, [readOnly, onDirtyChange])
  return null
}

export const EditorMount = forwardRef<unknown, EditorMountProps>(function EditorMount(
  { registration, mountKey, fileId, fileName, room, readOnly, previewOnly = false, notice, isActive, onDirtyChange, saveRef, discardRef, registerUndoRedo, extra, fallback = DefaultSpinner },
  ref,
) {
  const Component = useMemo(() => {
    if (!registration.defaultProps) return registration.component
    const Base = registration.component
    const defaults = registration.defaultProps
    return forwardRef<unknown, Record<string, unknown>>((props, r) => <Base ref={r} {...defaults} {...props} />)
  }, [registration])

  // Preview-only is read-only in every respect.
  const writable = !readOnly && !previewOnly
  return (
    <Suspense fallback={fallback}>
      {onDirtyChange && <ClearDirtyWhenReadOnly readOnly={!writable} onDirtyChange={onDirtyChange} />}
      <Component
        key={mountKey}
        ref={ref}
        {...extra}
        room={previewOnly ? '' : room}
        fileId={fileId}
        fileName={fileName}
        readOnly={!writable}
        isActive={isActive}
        {...(previewOnly ? { previewOnly: true, toolbarNotice: notice } : {})}
        onDirtyChange={writable ? onDirtyChange : undefined}
        onSaveRef={writable ? saveRef : undefined}
        onDiscardRef={writable ? discardRef : undefined}
        registerUndoRedo={writable ? registerUndoRedo : undefined}
      />
    </Suspense>
  )
})
